import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { celebrate } from "@/components/Celebrate";

/**
 * AchievementsRow — collectible "Sun badges" on the student dashboard.
 * Each badge is either locked (grayscale + dim) or unlocked (full color + rays twinkle).
 * Clicking an unlocked badge fires a small celebrate burst.
 */
export default function AchievementsRow() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/achievements/").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return null;

  return (
    <div className="brimi-card p-5" data-testid="achievements-row">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-bold">Sun Collection</p>
          <p className="font-display text-xl text-slate-900 mt-0.5">
            {data.unlocked_count} <span className="text-slate-400">of {data.total}</span> earned
          </p>
        </div>
        <div className="h-2 w-40 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full transition-all duration-500" style={{
            width: `${(data.unlocked_count / data.total) * 100}%`,
            background: "linear-gradient(90deg, #FB7185, #FBBF24, #34D399)",
          }} />
        </div>
      </div>

      <div className="mt-4 flex gap-3 overflow-x-auto pb-2 -mx-1 px-1">
        {data.achievements.map((a) => (
          <Badge key={a.id} a={a} />
        ))}
      </div>
    </div>
  );
}

function Badge({ a }) {
  const locked = !a.unlocked;
  const onClick = () => {
    if (!locked) celebrate(`${a.name} unlocked!`);
  };
  return (
    <button
      onClick={onClick}
      data-testid={`achievement-${a.id}`}
      className={`flex-shrink-0 w-24 p-3 rounded-2xl border transition text-center ${
        locked ? "border-slate-200 bg-slate-50" : "border-slate-200 bg-white hover:border-slate-900"
      }`}
      style={{ opacity: locked ? 0.55 : 1 }}
      title={a.desc}
    >
      <div
        className={`w-14 h-14 mx-auto rounded-full flex items-center justify-center relative ${locked ? "" : "brimi-badge-active"}`}
        style={{
          background: locked ? "#E2E8F0" : `radial-gradient(circle at 30% 30%, ${a.color}, ${a.color}88)`,
          boxShadow: locked ? "none" : `0 0 20px ${a.color}55`,
        }}
      >
        {/* Sun rays around the badge */}
        {!locked && (
          <>
            <span className="brimi-badge-ray" style={{ "--r": "0deg", background: a.color }} />
            <span className="brimi-badge-ray" style={{ "--r": "60deg", background: a.color }} />
            <span className="brimi-badge-ray" style={{ "--r": "120deg", background: a.color }} />
            <span className="brimi-badge-ray" style={{ "--r": "180deg", background: a.color }} />
            <span className="brimi-badge-ray" style={{ "--r": "240deg", background: a.color }} />
            <span className="brimi-badge-ray" style={{ "--r": "300deg", background: a.color }} />
          </>
        )}
        <svg viewBox="0 0 40 40" width="26" height="26" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id={`bg-${a.id}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={locked ? "#94A3B8" : "#FBBF24"} />
              <stop offset="100%" stopColor={locked ? "#64748B" : "#FB7185"} />
            </linearGradient>
          </defs>
          <path d="M11 24 A 9 9 0 0 1 29 24 Z" fill={`url(#bg-${a.id})`} />
          <line x1="6" y1="24.5" x2="34" y2="24.5" stroke={locked ? "#475569" : "#0F1B33"} strokeWidth="0.8" strokeLinecap="round" />
          <path d="M6 29 Q 12 26.5, 18 29 T 34 29" stroke={locked ? "#475569" : "#0F1B33"} strokeWidth="1.4" fill="none" strokeLinecap="round" />
        </svg>
      </div>
      <p className={`mt-2 text-[11px] font-bold uppercase tracking-wide ${locked ? "text-slate-500" : "text-slate-900"}`}>
        {a.name.replace(" Sun", "")}
      </p>
      {!locked && <p className="text-[9px] text-emerald-600 font-bold mt-0.5">✓ Earned</p>}
      {locked && <p className="text-[9px] text-slate-400 mt-0.5">Locked</p>}
    </button>
  );
}
